import { formatDate } from '@angular/common';
import { AbstractControl } from '@angular/forms';
import { MatMenuTrigger } from '@angular/material/menu';
import { ControlsOf, FormGroup } from '@ngneat/reactive-forms';
import { combineLatest, of } from 'rxjs';
import { catchError, first, map, switchMap, timeout } from 'rxjs/operators';
import { PlanningHasEntity } from '../classes/planning-has-entity.class';
import { Planning } from '../classes/planning.class';
import { UserPlanning } from '../classes/user-planning';
import { PlanningHasService } from '../services/planning/planning-has.service';
import { PlanningService } from '../services/planning/planning.service';
import { UserPlanningService } from '../services/planning/user-planning.service';
import { Utils } from '../utils.class';

export type EmployeeConflict = { planningId: number | null; begindate: Date; text: string };

type ConflictItem = {
    planning_id?: number;
    entity_id?: number;
    begindate: Date;
    enddate: Date;
    afas_project_id?: string;
};

// planning is null for a row in the current form
const toConflict = (item: ConflictItem, planning: Planning | null | undefined): EmployeeConflict => {
    const project =
        planning === null
            ? 'Dit planningsitem (nog niet opgeslagen)'
            : [planning?.worknumber ?? planning?.afas_project_id ?? item.afas_project_id, planning?.project?.name]
                  .filter(Boolean)
                  .join(' ') || 'Geen project';
    const team = planning?.mainPlanning?.entity?.name;
    const entity = planning?.planning_has?.find(ph => item.entity_id && ph.entity_id === item.entity_id)?.entity?.name;
    const from = formatDate(item.begindate, 'EEEE d MMM yyyy HH:mm', 'nl');
    const to = formatDate(item.enddate, 'HH:mm', 'nl');
    return {
        planningId: planning?.id ?? null,
        begindate: item.begindate,
        text: [project, team, entity !== team ? entity : null, `${from} - ${to}`].filter(Boolean).join(' – '),
    };
};

export const localEmployeeAvailableValidator = (
    userPlanningService?: UserPlanningService,
    planningHasService?: PlanningHasService,
    planningService?: PlanningService,
    skipUserPlanning?: UserPlanning,
    planningId?: number,
    currentFormPlanningHas?: () => Pick<PlanningHasEntity, 'id' | 'driver_user_id' | 'begindate' | 'enddate'>[],
    currentFormUserPlanning?: () => AbstractControl[]
) => {
    return (
        formGroup: FormGroup<
            ControlsOf<{
                id: number;
                conflictText: string;
                begindate: number;
                enddate: number;
                user_id: number;
            }>
        >
    ) => {
        if (formGroup?.value.begindate && formGroup?.value.enddate && formGroup?.value.user_id) {
            const begindate = Utils.getTimeOrNull(formGroup?.value.begindate as any as Date);
            const enddate = Utils.getTimeOrNull(formGroup?.value.enddate as any as Date);
            return combineLatest([
                userPlanningService.getFilteredList(
                    Utils.newDate(formGroup?.value.begindate),
                    Utils.newDate(formGroup?.value.enddate)
                ),
                planningHasService.getFilteredList(
                    Utils.newDate(formGroup?.value.begindate),
                    Utils.newDate(formGroup?.value.enddate)
                ),
            ]).pipe(
                first(),
                switchMap(([userPlannings, planningHass]) => {
                    const conflicts: { item: ConflictItem; currentForm: boolean }[] = [];

                    [
                        ...planningHass.filter(ph => ph?.planning_id !== planningId),
                        ...(currentFormPlanningHas ? currentFormPlanningHas() : []),
                    ].forEach(planningHas => {
                        if (planningHas.driver_user_id === formGroup?.value.user_id) {
                            if (
                                Utils.getTimeOrNull(planningHas.begindate) < enddate &&
                                Utils.getTimeOrNull(planningHas.enddate) > begindate
                            ) {
                                conflicts.push({
                                    item: planningHas,
                                    currentForm: !planningHass.some(ph => ph === planningHas),
                                });
                            }
                        }
                    });

                    [
                        ...userPlannings.filter(up => up?.planning_id !== planningId && up?.id !== formGroup.value.id),
                        ...(currentFormUserPlanning
                            ? currentFormUserPlanning()
                                  .filter(c => c !== formGroup)
                                  .map(c => c.value)
                            : []),
                    ]
                        .filter(up => skipUserPlanning?.id == null || skipUserPlanning.id !== up?.id)
                        .forEach(userPlanning => {
                            if (userPlanning.user_id === formGroup.value.user_id) {
                                if (
                                    Utils.getTimeOrNull(userPlanning.begindate) < enddate &&
                                    Utils.getTimeOrNull(userPlanning.enddate) > begindate
                                ) {
                                    conflicts.push({
                                        item: userPlanning,
                                        currentForm: !userPlannings.includes(userPlanning),
                                    });
                                }
                            }
                        });

                    if (!conflicts.length) {
                        formGroup.controls.conflictText.setValue(null, { emitEvent: false, onlySelf: true });
                        return of(null);
                    }

                    // Plannings are only needed for the conflict text; don't block validation when they don't load
                    return planningService
                        .getFilteredList(
                            Utils.newDate(formGroup?.value.begindate),
                            Utils.newDate(formGroup?.value.enddate)
                        )
                        .pipe(
                            first(),
                            timeout(3000),
                            catchError(() => of([] as Planning[])),
                            map(plannings => {
                                const result = conflicts.map(({ item, currentForm }) =>
                                    toConflict(
                                        item,
                                        currentForm ? null : plannings.find(p => p.id === item.planning_id)
                                    )
                                );

                                formGroup.controls.conflictText.setValue(
                                    `De inplanning conflicteert met:\n\n${result.map(c => c.text).join('\n\n')}`,
                                    { emitEvent: false, onlySelf: true }
                                );

                                return { notAvailable: result };
                            })
                        );
                })
            );
        }
        return of(null);
    };
};

/**
 * Ctrl/Cmd + click on a conflicting employee field: opens the single conflict, or a menu to choose one.
 * The click is caught on the document before it reaches the datetime input, which would open the datepicker.
 */
export const onConflictCtrlClick = (
    event: MouseEvent,
    control: AbstractControl,
    trigger: MatMenuTrigger,
    open: (conflict: EmployeeConflict) => void
) => {
    const conflicts: EmployeeConflict[] = control.errors?.notAvailable?.filter(c => c.planningId) ?? [];
    if (event.button !== 0 || !(event.ctrlKey || event.metaKey) || !conflicts.length) {
        return;
    }
    event.preventDefault();
    const listeners = new AbortController();
    const options = { capture: true, signal: listeners.signal };
    document.addEventListener(
        'click',
        click => {
            listeners.abort();
            click.stopPropagation();
            if (conflicts.length === 1) {
                open(conflicts[0]);
            } else {
                trigger.openMenu();
            }
        },
        options
    );
    document.addEventListener('contextmenu', () => listeners.abort(), options);
};
